import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  HStack,
  useToast,
  Divider,
  Image,
  SimpleGrid,
  Card,
  CardBody,
  Icon,
  Flex,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Select,
  Badge,
  Spinner
} from '@chakra-ui/react';
import { FaStore, FaMapMarkerAlt, FaPhone, FaEnvelope, FaClock, FaImage } from "react-icons/fa";
import axios from "axios";
import SelectLocation from "../../SellFormComponents/SelectLocation";
import { API_BASE_URL } from "../../config/api_keys";

const StoreForm = () => {
  const navigate = useNavigate();
  const toast = useToast();
  
  // Store form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    contactPhone: "",
    contactEmail: "",
    openingHours: ""
  });
  
  // Location state
  const [locationData, setLocationData] = useState({
    state: "",
    city: "",
    area: "",
    postcode: "",
    latitude: null,
    longitude: null
  });
  
  // Image upload state
  const [logo, setLogo] = useState(null);
  const [coverImage, setCoverImage] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  
  // Loading state
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  // Handle logo image selection
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogo(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Handle cover image selection
  const handleCoverChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCoverImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setCoverPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Handle location selection from SelectLocation component
  const handleLocationSelect = (data) => {
    const { location = {}, address = {}, coordinates = null } = data || {};
    
    setLocationData({
      state: location.state || address.state || "",
      city: location.city || address.city || "",
      area: location.area || address.area || "",
      postcode: address.postcode || "",
      latitude: coordinates ? coordinates.latitude : null,
      longitude: coordinates ? coordinates.longitude : null
    });
  };
  
  // Submit form to create store
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.name || !formData.description) {
      toast({
        title: "Missing information",
        description: "Please provide a store name and description",
        status: "error",
        duration: 5000,
        isClosable: true
      });
      return;
    }
    
    if (!locationData.state || !locationData.city || !locationData.latitude || !locationData.longitude) {
      toast({
        title: "Location required",
        description: "Please select a complete location with coordinates",
        status: "error",
        duration: 5000,
        isClosable: true
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Create form data for multipart/form-data (for file uploads)
      const formDataToSend = new FormData();
      
      // Add text fields
      formDataToSend.append("name", formData.name);
      formDataToSend.append("description", formData.description);
      formDataToSend.append("state", locationData.state);
      formDataToSend.append("city", locationData.city);
      formDataToSend.append("area", locationData.area);
      formDataToSend.append("postcode", locationData.postcode);
      formDataToSend.append("latitude", locationData.latitude);
      formDataToSend.append("longitude", locationData.longitude);
      formDataToSend.append("contactPhone", formData.contactPhone);
      formDataToSend.append("contactEmail", formData.contactEmail);
      formDataToSend.append("openingHours", formData.openingHours);
      
      // Add files if they exist
      if (logo) {
        formDataToSend.append("logo", logo);
      }
      
      if (coverImage) {
        formDataToSend.append("coverImage", coverImage);
      }
      
      // Get token from localStorage
      const token = localStorage.getItem("authToken");
      
      if (!token) {
        toast({
          title: "Authentication required",
          description: "Please log in to create a store",
          status: "error",
          duration: 5000,
          isClosable: true
        });
        navigate("/login");
        return;
      }
      
      // Send request to create store
      const response = await axios.post(
        `${API_BASE_URL}/api/stores`,
        formDataToSend,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        }
      );
      
      toast({
        title: 'Store created!',
        description: 'Your store has been submitted for verification. You will be notified once it is approved.',
        status: 'success',
        duration: 5000,
        isClosable: true
      });
      
      // Navigate to the new store page
      navigate(`/store/${response.data.store._id}`);
    } catch (error) {
      console.error("Error creating store:", error);
      
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to create store. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <Container maxW="container.md" py={8}>
      <VStack spacing={8} align="stretch">
        <Box textAlign="center">
          <Heading size="xl" mb={2} color="green.600">
            <Icon as={FaStore} mr={2} />
            Create Your Store
          </Heading>
          <Text color="gray.600">
            Set up your agricultural store to showcase and sell your products
          </Text>
          
          <Alert status="info" mt={4} borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle>Verification Required</AlertTitle>
              <AlertDescription>
                Your store will need to be verified by an admin before it becomes active and visible to customers.
                You will be notified once your store is approved.
              </AlertDescription>
            </Box>
          </Alert>
        </Box>
        
        <form onSubmit={handleSubmit}>
          <VStack spacing={6} align="stretch">
            {/* Store Basic Information */}
            <Card variant="outline">
              <CardBody>
                <VStack spacing={4} align="stretch">
                  <Heading size="md" color="green.600">Basic Information</Heading>
                  
                  <FormControl isRequired>
                    <FormLabel>Store Name</FormLabel>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter your store name"
                    />
                  </FormControl>
                  
                  <FormControl isRequired>
                    <FormLabel>Description</FormLabel>
                    <Textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Describe your store and what you sell"
                      rows={4}
                    />
                  </FormControl>
                </VStack>
              </CardBody>
            </Card>
            
            {/* Store Location */}
            <Card variant="outline">
              <CardBody>
                <VStack spacing={4} align="stretch">
                  <Heading size="md" color="green.600">
                    <Icon as={FaMapMarkerAlt} mr={2} />
                    Store Location
                  </Heading>
                  <Text fontSize="sm" color="gray.600">
                    Select your store location so customers can find you easily
                  </Text>
                  
                  <SelectLocation onAddressSelect={handleLocationSelect} />
                  
                  {locationData.latitude && locationData.longitude && (
                    <Box mt={2}>
                      <Text fontSize="sm" fontWeight="bold" color="green.600">
                        Location coordinates saved successfully
                      </Text>
                    </Box>
                  )}
                </VStack>
              </CardBody>
            </Card>
            
            {/* Contact Information */}
            <Card variant="outline">
              <CardBody>
                <VStack spacing={4} align="stretch">
                  <Heading size="md" color="green.600">Contact Information</Heading>
                  
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl>
                      <FormLabel>
                        <Icon as={FaPhone} mr={2} />
                        Contact Phone
                      </FormLabel>
                      <Input
                        name="contactPhone"
                        value={formData.contactPhone}
                        onChange={handleInputChange}
                        placeholder="Enter contact phone number"
                      />
                    </FormControl>
                    
                    <FormControl>
                      <FormLabel>
                        <Icon as={FaEnvelope} mr={2} />
                        Contact Email
                      </FormLabel>
                      <Input
                        name="contactEmail"
                        value={formData.contactEmail}
                        onChange={handleInputChange}
                        placeholder="Enter contact email"
                        type="email"
                      />
                    </FormControl>
                  </SimpleGrid>
                  
                  <FormControl>
                    <FormLabel>
                      <Icon as={FaClock} mr={2} />
                      Opening Hours
                    </FormLabel>
                    <Input
                      name="openingHours"
                      value={formData.openingHours}
                      onChange={handleInputChange}
                      placeholder="e.g., Mon-Fri: 9AM-5PM, Sat: 10AM-2PM"
                    />
                  </FormControl>
                </VStack>
              </CardBody>
            </Card>
            
            {/* Store Images */}
            <Card variant="outline">
              <CardBody>
                <VStack spacing={4} align="stretch">
                  <Heading size="md" color="green.600">
                    <Icon as={FaImage} mr={2} />
                    Store Images
                  </Heading>
                  
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                    {/* Logo Upload */}
                    <FormControl>
                      <FormLabel>Store Logo</FormLabel>
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoChange}
                        p={1}
                      />
                      {logoPreview && (
                        <Box mt={2} borderRadius="md" overflow="hidden" width="150px" height="150px">
                          <Image 
                            src={logoPreview} 
                            alt="Logo preview" 
                            objectFit="cover"
                            width="100%"
                            height="100%"
                          />
                        </Box>
                      )}
                    </FormControl>
                    
                    {/* Cover Image Upload */}
                    <FormControl>
                      <FormLabel>Cover Image</FormLabel>
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={handleCoverChange}
                        p={1}
                      />
                      {coverPreview && (
                        <Box mt={2} borderRadius="md" overflow="hidden" height="150px">
                          <Image 
                            src={coverPreview} 
                            alt="Cover image preview" 
                            objectFit="cover"
                            width="100%"
                            height="100%"
                          />
                        </Box>
                      )}
                    </FormControl>
                  </SimpleGrid>
                </VStack>
              </CardBody>
            </Card>
            
            <Divider />
            
            {/* Submit Button */}
            <Flex justify="center">
              <Button
                type="submit"
                colorScheme="green"
                size="lg"
                isLoading={isSubmitting}
                loadingText="Creating Store"
                leftIcon={<FaStore />}
                px={8}
              >
                Create Store
              </Button>
            </Flex>
          </VStack>
        </form>
      </VStack>
    </Container>
  );
};

export default StoreForm;